import React, { useCallback, useMemo, VoidFunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStandard } from 'record-it-components/button/ButtonStandard/ButtonStandard';
import { useModalOpened } from 'record-it-components/hooks/useModalOpened/useModalOpened';
import { Icon, IconType } from 'record-it-components/misc/Icon/Icon';
import {
  CreateEntityButtonConfig,
  HeaderConfig,
  ManageEntitiesContentLayout
} from 'record-it-components/pageContentLayout/ManageEntitiesContentLayout/ManageEntitiesContentLayout';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { WorkTimeEntry } from '../../../classes/entities/WorkTimeEntry/workTimeEntryTypes';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { ExportOwnWorkTimeDialog } from '../../workTime/ExportOwnWorkTimeDialog/ExportOwnWorkTimeDialog';
import { WorkTimeEntryList } from '../../workTime/WorkTimeEntryList/WorkTimeEntryList';

export type MyWorkTimeOverviewPageProps = PropsWithoutChildren<
  Record<never, never>
>;

export const MyWorkTimeOverviewPage: VoidFunctionComponent<MyWorkTimeOverviewPageProps> =
  React.memo((props) => {
    prepareProps({ props, ignoreChildren: true });

    useConfigureBreadcrumbsWithBuilder((builder) =>
      builder.addHomePage().addMyWorkTimeOverviewPage()
    );

    const { t } = useTranslation();
    const { getRequiredCurrentUser } = useCurrentUser();
    const workTimeEntries = useEntities(
      EntityName.WorkTimeEntry,
      'getByUserId',
      getRequiredCurrentUser().id
    );
    const sortedWorkTimeEntries = useMemo(() => {
      return workTimeEntries.sort((a, b) => b.endDate.localeCompare(a.endDate));
    }, [workTimeEntries]);

    const {
      close: closeExportModal,
      open: openExportModal,
      modalProps: exportModalProps
    } = useModalOpened({ closeOnBackDropClick: true });

    const createEntityButtonConfig: CreateEntityButtonConfig<unknown> = useMemo(
      () => ({
        visible: false
      }),
      []
    );

    const headerConfig: HeaderConfig = useMemo(() => {
      return {
        iconConfig:
          iconConfigurations.prefixed.page.MyWorkTimeOverviewPage.titleIcon,
        title: t('components.page.MyWorkTimeOverviewPage.pageTitle'),
        // eslint-disable-next-line react/no-multi-comp
        renderAdditionalRightContent: () => [
          <ButtonStandard
            key="MyWorkTimeOverviewPage.ExportOwnWorkTimeButton"
            onClick={openExportModal}
            data-testid="MyWorkTimeOverviewPage.ExportOwnWorkTimeButton"
          >
            <Icon
              type={IconType.Regular}
              name="file-export"
            ></Icon>
          </ButtonStandard>
        ]
      };
    }, [t, openExportModal]);

    const renderEntities = useCallback(
      (currentPageWorkTimeEntries: Array<WorkTimeEntry>) => {
        return (
          <WorkTimeEntryList
            workTimeEntries={currentPageWorkTimeEntries}
            data-testid="MyWorkTimeOverviewPage.WorkTimeEntryList"
          ></WorkTimeEntryList>
        );
      },
      []
    );

    return (
      <>
        <ManageEntitiesContentLayout<WorkTimeEntry, unknown>
          headerConfig={headerConfig}
          createEntityButtonConfig={createEntityButtonConfig}
          entities={sortedWorkTimeEntries}
          renderEntities={renderEntities}
        ></ManageEntitiesContentLayout>
        <ExportOwnWorkTimeDialog
          {...exportModalProps}
          onCancelClick={closeExportModal}
          data-testid="MyWorkTimeOverviewPage.ExportOwnWorkTimeDialog"
        ></ExportOwnWorkTimeDialog>
      </>
    );
  });
